/**
 * Store page data for Marketplace listings — icon, video, screenshots, reviews,
 * versions and MCP tools.
 *
 * None of this has a back end yet. What a publisher enters in the wizard's Store
 * page step is kept in this browser (localStorage, keyed by listing id), and with
 * REACT_APP_STORE_MOCK=true every listing without its own media also gets sample
 * content, so the page can be reviewed before the API exists. Each export here is
 * the seam a real endpoint replaces.
 */

export type StoreToolType = 'skill' | 'agent' | 'workflow' | 'widget' | 'mcp_server' | 'integration';

export interface StoreReview {
  id: string;
  reviewer: string;
  agency: string;
  rating: number;
  comment: string;
  date: string;
}

export interface StoreRelease {
  version: string;
  date: string;
  notes: string;
}

export interface StoreMcpTool {
  name: string;
  description: string;
}

/** What the publisher fills in on the Store page step. */
export interface StoreDraft {
  iconUrl?: string;
  videoUrl?: string;
  screenshots: string[];
  /** Agent listings only — the store copy, separate from the agent's own description. */
  storeDescription?: string;
  version?: string;
  releaseNotes?: string;
  /** Set while editing: the version currently live, so the new one can be compared to it. */
  lastVersion?: string;
}

export type StoreVideo =
  | { kind: 'embed'; url: string }
  | { kind: 'sample'; poster: string }
  | null;

export interface StoreListing {
  iconUrl?: string;
  video: StoreVideo;
  screenshots: string[];
  storeDescription?: string;
  pendingVersion?: string;
  pendingReleaseNotes?: string;
  reviews: StoreReview[];
  releases: StoreRelease[];
  mcpTools: StoreMcpTool[];
  /** True when any of the above is sample content rather than the publisher's own. */
  sample: boolean;
}

export interface StoreListingSource {
  id: string;
  name: string;
  type: StoreToolType;
  createdAt?: string;
  widgetVersion?: string;
}

const DRAFTS_KEY = 'ia-marketplace-store-drafts';
const REVIEWS_KEY = 'ia-marketplace-store-reviews';

export function isStoreMockEnabled(): boolean {
  return process.env.NODE_ENV !== 'production' && process.env.REACT_APP_STORE_MOCK === 'true';
}

export function emptyStoreDraft(): StoreDraft {
  return { screenshots: [] };
}

/** Editing a published listing starts a new version: bump the minor number, blank notes. */
export function draftForEditing(id: string): StoreDraft {
  const draft = getStoreDraft(id);
  if (!draft) return emptyStoreDraft();
  const last = draft.version || '1.0.0';
  const [major, minor] = last.split('.').map(n => parseInt(n, 10) || 0);
  return { ...draft, lastVersion: last, version: `${major}.${minor + 1}.0`, releaseNotes: '' };
}

function readJson<T>(key: string): Record<string, T> {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Record<string, T>) : {};
  } catch {
    return {};
  }
}

function writeJson<T>(key: string, value: Record<string, T>): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or private mode — the draft just isn't kept between visits.
  }
}

export function getStoreDraft(id: string): StoreDraft | null {
  return readJson<StoreDraft>(DRAFTS_KEY)[id] ?? null;
}

export function saveStoreDraft(id: string, draft: StoreDraft): void {
  const all = readJson<StoreDraft>(DRAFTS_KEY);
  all[id] = draft;
  writeJson(DRAFTS_KEY, all);
}

export function addStoreReview(id: string, review: Omit<StoreReview, 'id' | 'date'>): StoreReview {
  const all = readJson<StoreReview[]>(REVIEWS_KEY);
  const saved: StoreReview = { ...review, id: `local-${Date.now()}`, date: new Date().toISOString() };
  all[id] = [saved, ...(all[id] ?? [])];
  writeJson(REVIEWS_KEY, all);
  return saved;
}

/** Everything the store page shows for one listing: the publisher's own content first, then samples. */
export function getStoreListing(tool: StoreListingSource): StoreListing {
  const draft = getStoreDraft(tool.id) ?? emptyStoreDraft();
  const localReviews = readJson<StoreReview[]>(REVIEWS_KEY)[tool.id] ?? [];
  const mock = isStoreMockEnabled();
  const seed = hash(tool.id + tool.name);

  const ownVideo = draft.videoUrl ? { kind: 'embed' as const, url: draft.videoUrl } : null;
  const video: StoreVideo = ownVideo ?? (mock ? { kind: 'sample', poster: videoPosterSvg(tool.name, tool.type) } : null);
  const screenshots = draft.screenshots.length > 0
    ? draft.screenshots
    : mock ? [0, 1, 2].map(i => screenshotSvg(tool.name, tool.type, i)) : [];
  const sampleReviews = mock ? pickReviews(seed) : [];
  const ownReleases: StoreRelease[] = draft.version
    ? [{ version: draft.version, date: new Date().toISOString(), notes: draft.releaseNotes ?? '' }]
    : [];
  const releases = ownReleases.length > 0 ? ownReleases : mock ? sampleReleases(tool, seed) : [];

  return {
    iconUrl: draft.iconUrl,
    video,
    screenshots,
    storeDescription: draft.storeDescription,
    reviews: [...localReviews, ...sampleReviews],
    releases,
    mcpTools: tool.type === 'mcp_server' && mock ? sampleMcpTools(tool.name) : [],
    sample: mock && (!ownVideo || draft.screenshots.length === 0 || sampleReviews.length > 0),
  };
}

/** Stand-in for the AI listing draft endpoint: a description built from what the listing contains. */
export async function draftListingDescription(input: { name: string; type: StoreToolType; facts: string[] }): Promise<string> {
  await new Promise(resolve => setTimeout(resolve, 1200));
  const what: Record<StoreToolType, string> = {
    skill: 'gives your agents a repeatable way to handle',
    agent: 'is a ready-to-run Digital Workforce agent for',
    workflow: 'automates',
    widget: 'adds a live panel to Momentum AMP for',
    mcp_server: 'connects your agents to',
    integration: 'lets your agents call',
  };
  const facts = input.facts.filter(Boolean).slice(0, 5);
  const lines = [
    `**${input.name}** ${what[input.type]} the day-to-day work your CSRs and producers already do, so it gets done the same way every time.`,
    '',
    '**What it does**',
    '',
    ...(facts.length > 0 ? facts.map(f => `- ${f}`) : ['- Handles the task end to end, with no extra setup after install', '- Works with the data already in your agency']),
    '',
    '**Who it\'s for**',
    '',
    'Agencies that want this handled consistently without adding headcount. Install it for yourself to try it, then roll it out to your whole agency.',
  ];
  return lines.join('\n');
}

/**
 * Mock mode only: listings for types nobody has published on staging yet, so their
 * store pages can be reviewed. They exist only in this browser - ids start with
 * "sample-" and installing one does nothing.
 */
export interface SampleListing {
  id: string;
  type: StoreToolType;
  name: string;
  category: string;
  icon: string;
  description: string;
  widget?: { version: string; permissions: string[]; surfaces: string[]; preview: string };
  workflow?: { trigger: string; steps: string[] };
}

export function isSampleListing(id: string): boolean {
  return id.startsWith('sample-');
}

export function sampleListings(): SampleListing[] {
  if (!isStoreMockEnabled()) return [];
  return [
    {
      id: 'sample-widget',
      type: 'widget',
      name: 'Renewal Pipeline Board',
      category: 'Insurance',
      icon: '📊',
      description: 'See every renewal in the next 90 days on one board, sorted by how likely you are to lose it.\n\n**What it does**\n\n- Pulls upcoming renewals from your AMS\n- Flags premium jumps and missing contact info\n- Lets a CSR claim a renewal with one click',
      widget: { version: '2.1.0', permissions: ['api:policies.read', 'api:insureds.read', 'ui:notify'], surfaces: ['page', 'tile'], preview: screenshotSvg('Renewal Pipeline Board', 'widget', 1) },
    },
    {
      id: 'sample-workflow',
      type: 'workflow',
      name: 'New Lead Welcome Sequence',
      category: 'Communication',
      icon: '🔄',
      description: 'Welcomes every new web lead within minutes and gets them on a producer\'s calendar.\n\n**What it does**\n\n- Sends a welcome email from the assigned producer\n- Follows up by text if there\'s no reply\n- Creates a task so nothing falls through',
      workflow: {
        trigger: 'A new lead is created in NowCerts',
        steps: [
          'Send a welcome email from the assigned producer',
          'Wait 1 day',
          'Create a follow-up task for the producer',
          'If there\'s no reply in 3 days, send a text reminder',
          'Tag the lead "Nurture"',
        ],
      },
    },
  ];
}

export function getSampleListing(id: string): SampleListing | null {
  return sampleListings().find(s => s.id === id) ?? null;
}

// ── Sample content (mock mode only) ──

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

const REVIEW_POOL: Omit<StoreReview, 'id' | 'date'>[] = [
  { reviewer: 'Maria G.', agency: 'Lakeside Insurance Group', rating: 5, comment: 'Set it up for our CSR team in about ten minutes. Renewal follow-ups go out without anyone babysitting them.' },
  { reviewer: 'Tom H.', agency: 'Summit Risk Partners', rating: 4, comment: 'Does what it says. Would love a way to limit it to commercial lines only.' },
  { reviewer: 'Priya S.', agency: 'Harbor & Pine Insurance', rating: 5, comment: 'Our producers use it every day for quote prep. Easily an hour a day saved across the office.' },
  { reviewer: 'Derek L.', agency: 'Bluegrass Agency Co.', rating: 4, comment: 'Took a couple of tries to get the API key right, but the publisher answered quickly.' },
  { reviewer: 'Jen W.', agency: 'Northline Insurance Services', rating: 3, comment: 'Good start. The summaries are accurate, but the formatting could be cleaner.' },
  { reviewer: 'Carlos M.', agency: 'Redwood Coverage Partners', rating: 5, comment: 'Installed it for the whole agency in one click. Everyone had it the next morning.' },
];

function pickReviews(seed: number): StoreReview[] {
  const count = 3 + (seed % 3);
  const start = seed % REVIEW_POOL.length;
  const now = Date.now();
  return Array.from({ length: count }, (_, i) => {
    const r = REVIEW_POOL[(start + i) % REVIEW_POOL.length];
    return { ...r, id: `sample-${i}`, date: new Date(now - (i * 9 + (seed % 7) + 2) * 86400000).toISOString() };
  });
}

const RELEASE_NOTES = [
  'Faster responses on agencies with large policy lists.',
  'Added support for commercial lines.',
  'Fixed renewals that were skipped when the policy had no email on file.',
  'Clearer wording in the messages it sends to insureds.',
  'First release.',
];

function sampleReleases(tool: StoreListingSource, seed: number): StoreRelease[] {
  const [major, minor] = (tool.widgetVersion ?? `1.${2 + (seed % 3)}.0`).split('.').map(n => parseInt(n, 10) || 0);
  const now = Date.now();
  return [0, 1, 2].map(i => ({
    version: i === 2 ? `${major}.0.0` : `${major}.${Math.max(0, minor - i)}.0`,
    date: new Date(now - (i * 21 + 6) * 86400000).toISOString(),
    notes: i === 2 ? RELEASE_NOTES[4] : RELEASE_NOTES[(seed + i) % 4],
  }));
}

function sampleMcpTools(name: string): StoreMcpTool[] {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'server';
  return [
    { name: `${slug}_search`, description: 'Search records by name, number or keyword.' },
    { name: `${slug}_get`, description: 'Fetch one record with all of its details.' },
    { name: `${slug}_create`, description: 'Create a new record from what the agent has gathered.' },
  ];
}

function svgUri(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function escapeXml(s: string): string {
  return s.replace(/[<>&"']/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' }[c] as string));
}

const SHOT_TITLES: Record<StoreToolType, string[]> = {
  skill: ['Ask in plain English', 'Consistent answers every time', 'Works in any agent'],
  agent: ['Chat with your new agent', 'Hand it whole tasks', 'Review what it did'],
  workflow: ['Trigger on real events', 'Every step, in order', 'Runs in the background'],
  widget: ['Right on your dashboard', 'Live agency data', 'Set it up your way'],
  mcp_server: ['Connect once', 'Agents call it directly', 'See every call'],
  integration: ['Pick the endpoints', 'Agents call your API', 'Keys stay yours'],
};

function screenshotSvg(name: string, type: StoreToolType, index: number): string {
  const title = escapeXml(SHOT_TITLES[type][index % 3]);
  const app = escapeXml(name.slice(0, 40));
  const accent = ['#A200FF', '#2A65F2', '#00B0D3'][index % 3];
  const rows = Array.from({ length: 5 }, (_, i) => {
    const y = 300 + i * 74;
    const w = 520 - ((i * 97) % 220);
    return index === 0
      ? `<rect x="${i % 2 ? 640 : 380}" y="${y}" width="${w}" height="52" rx="18" fill="${i % 2 ? accent : '#2a2440'}" opacity="${i % 2 ? 0.85 : 1}"/>`
      : `<rect x="380" y="${y}" width="820" height="56" rx="10" fill="#221d33"/><rect x="404" y="${y + 20}" width="${w * 0.6}" height="14" rx="7" fill="#4a4266"/><rect x="${1060}" y="${y + 16}" width="110" height="24" rx="12" fill="${accent}" opacity="0.8"/>`;
  }).join('');
  return svgUri(`<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="800" viewBox="0 0 1280 800">
<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#381574"/><stop offset="1" stop-color="#0C0C0C"/></linearGradient></defs>
<rect width="1280" height="800" fill="url(#g)"/>
<text x="64" y="110" font-family="Poppins, Arial, sans-serif" font-size="46" font-weight="700" fill="#FAFAFA">${title}</text>
<text x="64" y="160" font-family="Poppins, Arial, sans-serif" font-size="24" fill="#c9b8ff">${app}</text>
<rect x="340" y="210" width="900" height="560" rx="22" fill="#15121f" stroke="#3b3358"/>
<rect x="340" y="210" width="900" height="54" rx="22" fill="#1d1830"/><circle cx="372" cy="237" r="7" fill="#ff5f57"/><circle cx="396" cy="237" r="7" fill="#febc2e"/><circle cx="420" cy="237" r="7" fill="#28c840"/>
${rows}
<rect x="64" y="230" width="230" height="440" rx="18" fill="#1d1830" stroke="#3b3358"/>
<rect x="88" y="262" width="150" height="16" rx="8" fill="${accent}"/><rect x="88" y="300" width="180" height="12" rx="6" fill="#4a4266"/><rect x="88" y="330" width="140" height="12" rx="6" fill="#4a4266"/><rect x="88" y="360" width="165" height="12" rx="6" fill="#4a4266"/>
</svg>`);
}

function videoPosterSvg(name: string, type: StoreToolType): string {
  const app = escapeXml(name.slice(0, 40));
  return svgUri(`<svg xmlns="http://www.w3.org/2000/svg" width="1280" height="720" viewBox="0 0 1280 720">
<defs><linearGradient id="v" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#A200FF"/><stop offset="1" stop-color="#381574"/></linearGradient></defs>
<rect width="1280" height="720" fill="url(#v)"/>
<circle cx="640" cy="330" r="74" fill="#FAFAFA" opacity="0.95"/><path d="M620 290 L620 370 L686 330 Z" fill="#381574"/>
<text x="640" y="480" text-anchor="middle" font-family="Poppins, Arial, sans-serif" font-size="40" font-weight="700" fill="#FAFAFA">${app}</text>
<text x="640" y="526" text-anchor="middle" font-family="Poppins, Arial, sans-serif" font-size="24" fill="#e6d9ff">See it in action · 1:42</text>
<text x="640" y="566" text-anchor="middle" font-family="Poppins, Arial, sans-serif" font-size="18" fill="#e6d9ff" opacity="0.8">${escapeXml(SHOT_TITLES[type][0])}</text>
</svg>`);
}
